import {
  Avatar,
  Grid,
  IconButton,
  Link,
  Tooltip,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import CustomDataGrid from '../../../common/components/dataGrid/CustomDataGrid'
import { Add as AddIcon } from '@mui/icons-material'
import DashboardLayout from '../../../common/layouts/DashboardLayout'
import { styled } from '@mui/system'

import {
  setColumnFilters,
  setServiceProvidersList,
} from '../../../store/slice/homeSlice'
import StatusChip from '../../../common/components/mui/Chips'
import { ServiceProviderColumn } from '../../../common/components/dataGrid/columns/HomeColumns'
import { getAllServiceProvider } from '../../../api/get/getApi'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { setAllProducts } from '../../../store/slice/commonSlice'
import {
  formatDate,
} from '../../../helpers/HelperFunctions'
import { useHomeRedux } from '../Home'
import { COMPANY_STATUS, COMPANY_STATUS_OBJ } from '../../../helpers/Constants'
import { useToastContext } from '../../../App'
import { setIsOrderActivatedView } from '../../../store/slice/homeSlice';
import OrderActivatedView from '../services/common/components/OrderActivatedView'
import ColumnFilterPopover from '../../../common/components/dataGrid/columns/ColumnFilterPopover'
import { getColumnFilterValues } from '../../../common/components/dataGrid/utils/getColumnFilterValues'
import { useGridFiltering } from '../../../common/components/dataGrid/utils/useGridFiltering'
import { toggleColumnFilterValue } from '../../../common/components/dataGrid/utils/toggleColumnFilter'

const FlexContainer = styled('div')({
  display: 'flex',
  flexWrap: 'nowrap',
  justifyContent: 'space-evenly',
  alignItems: 'center',
})

const Master = (props) => {
  const [filterAnchor, setFilterAnchor] = useState(null)
  const [activeColumn, setActiveColumn] = useState(null)

  // hooks
  const { dispatch, state } = useHomeRedux()
  const { toast } = useToastContext()

  const handleFilterClick = (event, column) => {
    setFilterAnchor(event.currentTarget)
    setActiveColumn(column)
  }
  useEffect(() => {
  dispatch(setColumnFilters({}));
}, []);

  const toggleFilterValue = (value) => {
    dispatch(
      setColumnFilters({
        ...state.columnFilters,
        [activeColumn]: toggleColumnFilterValue(
          state.columnFilters,
          activeColumn,
          value
        ),
      })
    )
  }
  //lifeCycles
  useEffect(() => {
    const OrderActivated = localStorage.getItem('OrderActivated')

    if (OrderActivated === 'true')
      dispatch(setIsOrderActivatedView(true))

    dispatch(setAllProducts(null))
    // state.searchValue?.serviceprovider && getFilteredRows()
    // if (!state.serviceProvidersList) fetchServiceProvidersList()

    if (!state.serviceProvidersList?.length) {
      fetchServiceProvidersList()
    }
  }, [state.searchValue?.serviceprovider, state?.serviceProvidersList])

  const fetchServiceProvidersList = async () => {
    const response = await getAllServiceProvider()
    const serviceProvidersList = response.data.result?.map((item) => ({
      ...item,
      createdDateTime: formatDate(new Date(item?.createdDateTime)),
    }))
    dispatch(setServiceProvidersList(serviceProvidersList))
    // dispatch(setServiceProvidersList(response.data || []))

  }

  const handleCopy = async (url) => {
    try {
      const permissionStatus = await navigator.permissions.query({
        name: 'clipboard-write',
      })

      if (
        permissionStatus.state === 'granted' ||
        permissionStatus.state === 'prompt'
      ) {
        await navigator.clipboard.writeText(url)
        toast.showToast('Text copied to clipboard successfully!')
      } else {
        toast.showToast('Clipboard write permission denied', 'error')
      }
    } catch (err) {
      toast.showToast(`Failed to copy text: ${url}`, 'error')
    }
  }

  const renderCompanyName = (row, value) => {
    return [COMPANY_STATUS.Active, COMPANY_STATUS.ApprovalPending].includes(row?.companyStatus) ? (
      <Typography variant="tablePrimary">
        <Link
          underline="none"
          color="primary"
          onClick={() => props?.onRowClick(row)}
        >
          {value}
        </Link>
        <Tooltip title="Copy Login Url">
          <IconButton onClick={() => handleCopy(row?.loginUrl)}>
            <ContentCopyIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Typography>
    ) : (
      <Typography variant="tablePrimary">
        {value}&nbsp;
        <Tooltip title="Copy Signup Url">
          <IconButton onClick={() => handleCopy(row?.signupUrl)}>
            <ContentCopyIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Typography>
    )
  }

  //render company
  const renderCompany = (params) => {
    const { row, value } = params
    return (
      <FlexContainer sx={{ gap: '1rem', justifyContent: 'center' }}>
        <Avatar alt="logo" src={row?.logoUrl} sx={{ width: 24, height: 24 }} />
        {renderCompanyName(row, value)}
      </FlexContainer>
    )
  }

  const renderCompanyStatus = (statusId, label) => {
    const statusObj = COMPANY_STATUS_OBJ.find((item) => item.value === statusId)
    return (
      <StatusChip
        label={statusId === COMPANY_STATUS.ApprovalPending ? 'Approval Pending' : label || statusObj?.name || 'N/A'}
        variant="outlined"
        color={statusObj?.color || 'default'}
        isSharp
      />
    )
  }
  const serviceProviderColumnProps = {
    renderFilter: () => { },
    renderCompany: renderCompany,
    renderCompanyStatus: renderCompanyStatus,
    onFilterClick: handleFilterClick,
    activeFilters: state.columnFilters,
  }

  const filteredData = useGridFiltering({
    rows: state.serviceProvidersList,
    columns: ServiceProviderColumn(serviceProviderColumnProps),
    searchValue: state.searchValue?.serviceprovider,
    columnFilters: state.columnFilters,
  })


  const columnValues = useMemo(
    () =>
      getColumnFilterValues({
        rows: state.serviceProvidersList,
        field: activeColumn,
        statusOptions: COMPANY_STATUS_OBJ,
      }),
    [activeColumn, state.serviceProvidersList]
  )

  const isLoading =
    !Array.isArray(state.serviceProvidersList)
      ? true
      : state.serviceProvidersList.length === 0

  const isAnyColumnFiltered = useMemo(() => {
    return Object.values(state.columnFilters || {}).some(
      (v) => Array.isArray(v) && v.length > 0
    )
  }, [state.columnFilters])

  const clearColumnFilter = () => {
    if (!activeColumn) return

    dispatch(
      setColumnFilters({
        ...state.columnFilters,
        [activeColumn]: [],
      })
    )
    setFilterAnchor(null)
    setActiveColumn(null)
  }
  const activeColumnLabel = useMemo(() => {
    if (!activeColumn) return ''

    const col = ServiceProviderColumn(serviceProviderColumnProps).find(
      (c) => c.field === activeColumn
    )

    return col?.headerName || activeColumn
  }, [activeColumn])
  return (
    state.isOrderActivatedView ? <OrderActivatedView /> :
      <>
        <Grid container>
          <DashboardLayout
            name="Service Provider"
            buttonStartIcon={<AddIcon />}
            // isButtonShow={false}
            handleButtonClick={(e) =>
              props?.handleShowCompany(e, 'serviceprovider')
            }
            children={
              <>
                <CustomDataGrid
                  gridKey='master'
                  columns={ServiceProviderColumn(serviceProviderColumnProps)}
                  filteredCount={filteredData.length}
                  data={filteredData}
                  loading={isLoading}
                  noDataText="Service Providers"
                  isFiltered={isAnyColumnFiltered}
                  totalCount={state.serviceProvidersList.length}
                  componentsProps={{
                    pagination: {
                      isFiltered:isAnyColumnFiltered,
                      totalCount:state.serviceProvidersList.length,
                      filteredCount:filteredData.length,
                    },
                  }}
                />

              </>
            }
          />
        </Grid>
        {activeColumn && (
          <ColumnFilterPopover
            anchorEl={filterAnchor}
            onClose={() => {
              setFilterAnchor(null)
              setActiveColumn(null)
            }}
            title={`Filter by ${activeColumnLabel}`}
            values={columnValues}
            selectedValues={state.columnFilters[activeColumn] || []}
            onChange={toggleFilterValue}
            onClear={clearColumnFilter}
          />
        )}
      </>
  )
}

export default Master
